import { useRef } from 'react';
import _get from 'lodash/get';
import { FormRefType } from '@lib/components/ReactHookForm/types';

// Reads `{ value }[]`-shaped (or, for single-select fields, a bare
// `{ value }`-shaped) form field state from a FormRef and returns just the
// string IDs. Used by every *ModalTableMultiselect wrapper to project the
// form value into the shape the base ModalTableMultiselect expects.
//
// `formRef.current.values` is mutated in place by react-hook-form, so a
// `useMemo([formValues])` would treat it as unchanged and cache the previous
// IDs forever. We re-read on every render and use a content-equality cache to
// keep the returned array reference stable when the IDs are unchanged.
export default function useSelectedFormFieldIds(
  formRef: FormRefType,
  fieldName: string,
): string[] {
  const cacheRef = useRef<string[]>([]);

  const fieldValues = _get(formRef.current?.values, fieldName);
  let next: string[];
  if (Array.isArray(fieldValues)) {
    next = fieldValues.map(({ value }) => value) as string[];
  } else if (fieldValues && typeof fieldValues === 'object') {
    const singleValue = _get(fieldValues, 'value');
    next =
      singleValue !== undefined && singleValue !== null ? [singleValue] : [];
  } else {
    next = [];
  }

  const prev = cacheRef.current;
  const isSame =
    prev.length === next.length && prev.every((v, i) => v === next[i]);

  if (!isSame) {
    cacheRef.current = next;
  }
  return cacheRef.current;
}
